import { useState, useRef, useEffect } from 'react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  eager?: boolean;
  placeholderColor?: string;
}

export default function LazyImage({
  src,
  alt,
  eager = false,
  className = '',
  placeholderColor = '#f3f3f0',
  style,
  ...props
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(eager);
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (eager) return;
    const el = ref.current;
    if (!el) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        },
        { rootMargin: '300px' }
      );
      observer.observe(el);
      return () => observer.disconnect();
    } else {
      setInView(true);
    }
  }, [eager]);

  return (
    <img
      ref={ref}
      src={inView ? src : undefined}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding={eager ? 'sync' : 'async'}
      fetchPriority={eager ? 'high' : 'auto'}
      onLoad={() => setLoaded(true)}
      className={`transition-opacity duration-400 ${loaded || eager ? 'opacity-100' : 'opacity-0'} ${className}`}
      style={{
        backgroundColor: !loaded ? placeholderColor : undefined,
        ...style,
      }}
      {...props}
    />
  );
}
